import { INCIDENT_KIND_LABELS, type CaseAlert } from '@lia/contracts';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { StatusBadge } from '../primitives/badge.js';
import { Button } from '../primitives/button.js';
import { FormField } from '../primitives/form-field.js';
import { Input, Textarea } from '../primitives/input.js';

// Supervision (v1 §10.9): an alert row (project, reason, waiting time, who has it or «sin tomar») and the documented
// resume of a temporary intervention («Tomar caso» → note + next step). Presentational only: the screen wires actions.

export function formatWaiting(minutes: number): string {
  if (minutes < 1) return 'hace menos de 1 min';
  if (minutes < 60) return `hace ${minutes} min`;
  const h = Math.floor(minutes / 60);
  if (h < 48) return `hace ${h} h ${minutes % 60 ? `${minutes % 60} min` : ''}`.trim();
  return `hace ${Math.floor(h / 24)} días`;
}

export interface CaseAlertRowProps {
  alert: Pick<CaseAlert, 'critical' | 'kind' | 'reason' | 'projectName' | 'contactName' | 'ownerName' | 'taken' | 'overdue' | 'waitingMinutes'>;
  /** Action slot (open, «Tomar caso»). */
  actions?: React.ReactNode;
  className?: string;
}

export function CaseAlertRow({ alert: a, actions, className }: CaseAlertRowProps) {
  return (
    <div className={cn('flex flex-wrap items-start gap-3 py-3', className)}>
      <span className="inline-flex flex-wrap gap-1">
        {a.critical ? <StatusBadge tone="danger">Crítica</StatusBadge> : <StatusBadge tone="warning">Incidencia</StatusBadge>}
        {a.taken ? <StatusBadge tone="info">Tomada</StatusBadge> : <StatusBadge tone={a.overdue ? 'danger' : 'warning'}>{a.overdue ? 'Sin tomar · plazo vencido' : 'Sin tomar'}</StatusBadge>}
      </span>
      <div className="grid min-w-0 flex-1 gap-0.5">
        <p className="text-body font-medium text-text-primary">
          {a.projectName} · {INCIDENT_KIND_LABELS[a.kind]}
          {a.contactName ? ` · ${a.contactName}` : ''}
        </p>
        <p className="text-body text-text-secondary">{a.reason}</p>
        <p className="text-meta text-text-secondary">
          {formatWaiting(a.waitingMinutes)} · {a.ownerName ? `${a.taken ? 'Atiende' : 'Asignada a'}: ${a.ownerName}` : 'Sin responsable'}
        </p>
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

export interface ResumeCaseFormProps {
  onResume: (v: { note: string; nextStep: string; nextStepAt: string | null }) => void;
  busy?: boolean;
  className?: string;
}

/** «Reanudar a LIA»: a note on what happened and the next step (optional date) — both required to resume. */
export function ResumeCaseForm({ onResume, busy, className }: ResumeCaseFormProps) {
  const [note, setNote] = React.useState('');
  const [nextStep, setNextStep] = React.useState('');
  const [at, setAt] = React.useState('');
  const ready = note.trim().length >= 3 && nextStep.trim().length >= 3;
  return (
    <form
      className={cn('grid gap-3', className)}
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) onResume({ note: note.trim(), nextStep: nextStep.trim(), nextStepAt: at ? new Date(at).toISOString() : null });
      }}
    >
      <FormField label="Nota de la intervención" description="Qué se hizo o acordó con el prospecto.">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={3} disabled={busy} />
      </FormField>
      <FormField label="Siguiente paso" description="LIA lo registra como la siguiente acción de la oportunidad.">
        <Input value={nextStep} onChange={(e) => setNextStep(e.target.value)} maxLength={300} disabled={busy} />
      </FormField>
      <FormField label="Fecha del siguiente paso (opcional)">
        <Input type="datetime-local" className="w-64" value={at} onChange={(e) => setAt(e.target.value)} disabled={busy} />
      </FormField>
      <div>
        <Button type="submit" disabled={!ready || busy}>
          Reanudar a LIA
        </Button>
      </div>
    </form>
  );
}
